import {
    allMedicationCounterMeasures,
    GetPatientOverviewSeveritiesSpecification,
    MedicationCounterMeasures,
    MedicationRiskCounterMeasure,
    QuestionnaireTimestamp,
    UpdateCounterMeasureSpecification
} from "pokal_api"
import { useEffect, useState } from "react"
import counterMeasureQuestionnaire, {
    CounterMeasureQuestion
} from "../../../medication_questionnaire/questionnaires/CounterMeasureQuestionnaire"
import notNull from "../../../util/notNull"
import Button from "../../Button"
import { dispatchThunk } from "../../../redux/thunk/thunkify"
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks"
import TextField from "../../TextField"
import ParentControlledCheckBox from "../../ParentControlledCheckBox"
import { selectDashboardSelectedPatient } from "../../../redux/ProfessionalDashboard"
import MedicationCounterMeasureNames from "../../../medication_questionnaire/abstract/MedicationCounterMeasureNames"

interface SingleCounterMeasureViewProps {
    counterMeasure: MedicationRiskCounterMeasure
}

function SingleCounterMeasureView(props: SingleCounterMeasureViewProps) {
    const [questionnaire, setQuestionnaire] =
        useState<null | CounterMeasureQuestion>(null)

    useEffect(() => {
        if (props.counterMeasure.counterMeasureRequired === null) {
            setQuestionnaire(
                counterMeasureQuestionnaire(props.counterMeasure.timestamp)[0]
            )
        } else {
            setQuestionnaire(null)
        }
    }, [props.counterMeasure])

    const dispatch = useAppDispatch()

    const [questionnairePath, setQuestionnairePath] = useState<
        { question: string; answer: string }[]
    >([])

    function onAnswer(
        result: CounterMeasureQuestion | boolean,
        question: string,
        answer: string
    ) {
        const currentQuestionnairePath = [
            ...questionnairePath,
            { answer, question }
        ]
        if (typeof result === "boolean") {
            dispatchThunk(dispatch, UpdateCounterMeasureSpecification, {
                id: props.counterMeasure.id,
                counterMeasureRequired: result as boolean,
                questionnairePath: JSON.stringify(currentQuestionnairePath)
            })
        } else setQuestionnaire(result as CounterMeasureQuestion)
        setQuestionnairePath(currentQuestionnairePath)
    }

    const [elseText, setElseText] = useState<string>("")

    useEffect(() => {
        if (
            notNull(props.counterMeasure.counterMeasureOther) &&
            props.counterMeasure.counterMeasureOther
        )
            setElseText(props.counterMeasure.counterMeasureOther)
    }, [props.counterMeasure])

    const [selectedCounterMeasure, setSelectedCounterMeasure] =
        useState<MedicationCounterMeasures | null>(null)

    const patient = useAppSelector(selectDashboardSelectedPatient)

    function onSave() {
        const success = window.confirm(
            "Sind Sie sicher, dass Sie die Auswahl absenden wollen? Sie können das später nicht mehr ändern."
        )
        if (success && notNull(patient)) {
            dispatchThunk(
                dispatch,
                UpdateCounterMeasureSpecification,
                {
                    id: props.counterMeasure.id,
                    measure: selectedCounterMeasure,
                    text:
                        selectedCounterMeasure ===
                        MedicationCounterMeasures.ELSE
                            ? elseText
                            : undefined
                },
                {
                    do: () =>
                        dispatchThunk(
                            dispatch,
                            GetPatientOverviewSeveritiesSpecification,
                            { patient: patient.id }
                        )
                }
            )
        }
    }

    return (
        <div key={props.counterMeasure.id}>
            <div className={"font-bold"}>{props.counterMeasure.atc.name}</div>
            {notNull(props.counterMeasure.counterMeasure) && (
                <div className={"flex space-x-1"}>
                    <div>
                        {
                            MedicationCounterMeasureNames[
                                props.counterMeasure.counterMeasure
                            ]
                        }
                    </div>
                    {props.counterMeasure.counterMeasure ===
                        MedicationCounterMeasures.ELSE &&
                        notNull(props.counterMeasure.counterMeasureOther) && (
                            <div>{`: ${props.counterMeasure.counterMeasureOther}`}</div>
                        )}
                </div>
            )}
            {props.counterMeasure.counterMeasureRequired !== null &&
                !props.counterMeasure.counterMeasureRequired && (
                    <div>
                        {props.counterMeasure.timestamp ===
                            QuestionnaireTimestamp.T0 ? "Keine Maßnahme geplant" : "Keine Maßnahme geplant oder Maßnahme wurde / wird durchgeführt."}
                    </div>
                )}
            {notNull(questionnaire) && (
                <div>
                    <div>{questionnaire.content}</div>
                    <div className={"flex space-x-2"}>
                        {questionnaire.onYes !== undefined && (
                            <Button
                                onClick={() =>
                                    onAnswer(
                                        questionnaire.onYes,
                                        questionnaire.content,
                                        "Ja"
                                    )
                                }>
                                {"Ja"}
                            </Button>
                        )}
                        {questionnaire.customYes !== undefined &&
                            questionnaire.onYes !== undefined && (
                                <Button
                                    onClick={() =>
                                        onAnswer(
                                            questionnaire.onYes,
                                            questionnaire.content,
                                            String(questionnaire.customYes)
                                        )
                                    }>
                                    {questionnaire.customYes}
                                </Button>
                            )}
                        {questionnaire.onNo !== undefined && (
                            <Button
                                onClick={() =>
                                    onAnswer(
                                        questionnaire.onNo,
                                        questionnaire.content,
                                        "Nein"
                                    )
                                }>
                                Nein
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {notNull(props.counterMeasure.counterMeasureRequired) &&
                props.counterMeasure.counterMeasureRequired &&
                props.counterMeasure.counterMeasure === null && (
                    <div className={"flex flex-col space-y-4 mt-8"}>
                        {allMedicationCounterMeasures.map((m) => (
                            <div key={m}>
                                <ParentControlledCheckBox
                                    value={selectedCounterMeasure === m}
                                    onChange={() =>
                                        setSelectedCounterMeasure(m)
                                    }>
                                    {MedicationCounterMeasureNames[m]}
                                </ParentControlledCheckBox>
                            </div>
                        ))}
                        <div className={"flex space-x-4"}>
                            {selectedCounterMeasure ===
                                MedicationCounterMeasures.ELSE && (
                                <div className={"flex space-x-2"}>
                                    <TextField
                                        placeholder={"Andere Maßnahme"}
                                        type={"text"}
                                        inputState={[elseText, setElseText]}
                                    />
                                </div>
                            )}
                            <Button onClick={onSave}>Speichern</Button>
                        </div>
                    </div>
                )}
        </div>
    )
}

export default SingleCounterMeasureView
